import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { userService } from "../../_services";
import ConfirmPopup from "./ConfirmPopup.jsx";

function WisePayment({ closePaymentPopup }) {
    const [address, setAddress] = useState('');
    const [country, setCountry] = useState('US');
    const [state, setState] = useState('');
    const [city, setCity] = useState('');
    const [zip, setZip] = useState('');
    const [routingNumber, setRoutingNumber] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
    const [accountType, setAccountType] = useState('checking');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [userId, setUserId] = useState('');

    useEffect(() => {
        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            setUserId(Usermeta_Data.split(':')[0]);
        } catch (error) {
            console.error('Error getting user ID:', error);
        }
    }, []);

    const validateForm = () => {
        const newErrors = {};

        if (!address.trim()) newErrors.address = 'Address is required';
        if (!city.trim()) newErrors.city = 'City is required';
        if (!state.trim()) newErrors.state = 'State is required';
        if (!zip.trim()) newErrors.zip = 'ZIP code is required';
        if (zip.trim() && !/^\d{5}(-\d{4})?$/.test(zip)) newErrors.zip = 'Invalid ZIP code';
        if (!routingNumber.trim()) newErrors.routingNumber = 'Routing number is required';
        if (routingNumber.trim() && !/^\d{9}$/.test(routingNumber)) newErrors.routingNumber = 'Routing number must be 9 digits';
        if (!accountNumber.trim()) newErrors.accountNumber = 'Account number is required';
        if (!confirmAccountNumber.trim()) newErrors.confirmAccountNumber = 'Please confirm account number';
        if (accountNumber !== confirmAccountNumber) newErrors.confirmAccountNumber = 'Account numbers do not match';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setShowConfirmPopup(true);
    };

    const confirmSubmit = async () => {
        setLoading(true);
        setErrors({});
        setSuccess('');

        try {
            // PRESERVE EXACT API CALL - DO NOT CHANGE
            const apidata = await userService.SaveWisePayment(
                userId,
                address,
                country,
                state,
                city,
                zip,
                routingNumber,
                accountNumber,
                accountType
            );

            if (apidata && apidata[0].code === "1") {
                setSuccess('Payment information saved successfully!');
                setLoading(false);
                setShowConfirmPopup(false);
                
                setTimeout(() => {
                    if (closePaymentPopup) {
                        closePaymentPopup();
                    }
                }, 2000);
            } else {
                setErrors({ api: apidata?.[0]?.message || 'Failed to save payment information' });
                setLoading(false);
                setShowConfirmPopup(false);
            }
        } catch (error) {
            console.error('Error saving payment:', error);
            setErrors({ api: 'An error occurred. Please try again.' });
            setLoading(false);
            setShowConfirmPopup(false);
        }
    };

    return (
        <>
            <Dialog open={true} onOpenChange={closePaymentPopup}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Set Up Payment Information</DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* API Error */}
                        {errors.api && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{errors.api}</AlertDescription>
                            </Alert>
                        )}

                        {/* Success Message */}
                        {success && (
                            <Alert>
                                <CheckCircle className="h-4 w-4" />
                                <AlertDescription>{success}</AlertDescription>
                            </Alert>
                        )}

                        {/* Address Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Address Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="address">Street Address</Label>
                                    <Input
                                        id="address"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        placeholder="123 Main St"
                                        className={errors.address ? 'border-red-500' : ''}
                                    />
                                    {errors.address && (
                                        <p className="text-sm text-red-600">{errors.address}</p>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="city">City</Label>
                                        <Input
                                            id="city"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            placeholder="City"
                                            className={errors.city ? 'border-red-500' : ''}
                                        />
                                        {errors.city && (
                                            <p className="text-sm text-red-600">{errors.city}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="state">State</Label>
                                        <Input
                                            id="state"
                                            value={state}
                                            onChange={(e) => setState(e.target.value)}
                                            placeholder="State"
                                            maxLength={2}
                                            className={errors.state ? 'border-red-500' : ''}
                                        />
                                        {errors.state && (
                                            <p className="text-sm text-red-600">{errors.state}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="zip">ZIP Code</Label>
                                        <Input
                                            id="zip"
                                            value={zip}
                                            onChange={(e) => setZip(e.target.value)}
                                            placeholder="12345"
                                            maxLength={10}
                                            className={errors.zip ? 'border-red-500' : ''}
                                        />
                                        {errors.zip && (
                                            <p className="text-sm text-red-600">{errors.zip}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="country">Country</Label>
                                        <Select value={country} onValueChange={setCountry}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="US">United States</SelectItem>
                                                <SelectItem value="CA">Canada</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Bank Account Section */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Bank Account Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="accountType">Account Type</Label>
                                    <Select value={accountType} onValueChange={setAccountType}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="checking">Checking</SelectItem>
                                            <SelectItem value="savings">Savings</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="routingNumber">Routing Number</Label>
                                    <Input
                                        id="routingNumber"
                                        value={routingNumber}
                                        onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
                                        placeholder="9 digits"
                                        maxLength={9}
                                        className={errors.routingNumber ? 'border-red-500' : ''}
                                    />
                                    {errors.routingNumber && (
                                        <p className="text-sm text-red-600">{errors.routingNumber}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="accountNumber">Account Number</Label>
                                    <Input
                                        id="accountNumber"
                                        type="password"
                                        value={accountNumber}
                                        onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                                        placeholder="Account number"
                                        className={errors.accountNumber ? 'border-red-500' : ''}
                                    />
                                    {errors.accountNumber && (
                                        <p className="text-sm text-red-600">{errors.accountNumber}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmAccountNumber">Confirm Account Number</Label>
                                    <Input
                                        id="confirmAccountNumber"
                                        type="password"
                                        value={confirmAccountNumber}
                                        onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, ''))}
                                        placeholder="Re-enter account number"
                                        className={errors.confirmAccountNumber ? 'border-red-500' : ''}
                                    />
                                    {errors.confirmAccountNumber && (
                                        <p className="text-sm text-red-600">{errors.confirmAccountNumber}</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Action Buttons */}
                        <div className="flex gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={closePaymentPopup}
                                className="flex-1"
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="secondary"
                                className="flex-1"
                                disabled={loading}
                            >
                                {loading ? (
                                    <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                                ) : (
                                    'Save Payment Info'
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Confirmation Popup */}
            {showConfirmPopup && (
                <ConfirmPopup
                    open={showConfirmPopup}
                    onClose={() => setShowConfirmPopup(false)}
                    confirmYes={confirmSubmit}
                    UserId={userId}
                    Address={address}
                    Country={country}
                    State={state}
                    City={city}
                    Zip={zip}
                    RoutingNumber={routingNumber}
                    AccountNumber={accountNumber}
                    AccountType={accountType}
                />
            )}
        </>
    );
}

export default WisePayment;
